import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useEnquiry } from "../site/EnquiryContext";

const VALUES = ["Curated professional matching", "Streamlined engagement coordination", "Senior expertise across jurisdictions"];
const FLOW = ["Requirement", "Matching", "Shortlist", "Engagement"];

const HeroVisual = () => (
  <div className="relative">
    <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-[#0B1220] sm:aspect-[5/5] lg:aspect-[4/5]">
      <img src="/images/hero.jpg" alt="Business leaders standing at a window overlooking a city skyline" className="h-full w-full object-cover opacity-90" loading="eager" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1220]/70 via-[#0B1220]/10 to-transparent" />
      <svg viewBox="0 0 400 500" className="absolute inset-0 h-full w-full" aria-hidden="true" fill="none">
        <path className="draw-line" d="M30 420 C 120 380, 170 260, 260 230 S 360 120, 380 60" stroke="#2563EB" strokeWidth="1.5" />
        <circle cx="380" cy="60" r="4" fill="#2563EB" />
        <circle cx="30" cy="420" r="3" fill="#fff" />
      </svg>
    </div>
    <div className="absolute -bottom-8 left-4 right-4 rounded-lg border border-slate-200 bg-white/95 p-5 shadow-[0_24px_60px_-24px_rgba(11,18,32,0.35)] backdrop-blur sm:left-auto sm:right-[-24px] sm:w-[320px]" data-testid="hero-process-card">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">The engagement path</p>
      <ol className="mt-4 grid grid-cols-4">
        {FLOW.map((f, i) => (
          <li key={f} className="flex flex-col items-start pr-1">
            <span className="flex w-full items-center">
              <span className={`h-2.5 w-2.5 rounded-full ${i === FLOW.length - 1 ? "bg-[#2563EB]" : "bg-[#0B1220]"}`} />
              {i < FLOW.length - 1 && <span className="h-px flex-1 bg-slate-300" />}
            </span>
            <span className="mt-2 text-[10.5px] font-medium text-[#111827]">{f}</span>
          </li>
        ))}
      </ol>
    </div>
  </div>
);

export const Hero = () => {
  const { openEnquiry } = useEnquiry();
  const reduce = useReducedMotion();
  const fade = (d) => (reduce ? {} : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] } });

  return (
    <section className="relative overflow-hidden bg-white" data-testid="home-hero">
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="container-x relative grid items-center gap-16 pb-28 pt-16 lg:grid-cols-12 lg:gap-12 lg:pb-32 lg:pt-24">
        <div className="lg:col-span-7 lg:pr-8">
          <motion.p {...fade(0)} className="eyebrow">Global professional services network</motion.p>
          <motion.h1 {...fade(0.08)} className="mt-7 font-display text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.035em] text-[#0B1220] sm:text-6xl lg:text-[4rem] xl:text-[4.25rem]">
            Exceptional expertise. <span className="text-slate-400">Without the traditional overhead.</span>
          </motion.h1>
          <motion.p {...fade(0.16)} className="lead mt-8 max-w-[38rem]">
            Trusted Leap connects ambitious businesses with vetted independent professionals across auditing, consulting, advisory, tax, and legal services. Find the right expertise for complex challenges through a curated, streamlined engagement experience.
          </motion.p>
          <motion.div {...fade(0.24)} className="mt-11 flex flex-wrap gap-4">
            <button type="button" onClick={() => openEnquiry()} className="btn-primary" data-testid="hero-request-expert-button">Request an Expert <ArrowRight className="h-4 w-4" /></button>
            <Link to="/for-experts#apply" className="btn-secondary" data-testid="hero-join-expert-link">Join as an Expert</Link>
          </motion.div>
          <motion.ul {...fade(0.32)} className="mt-14 grid gap-4 border-t border-slate-200 pt-8 sm:grid-cols-3">
            {VALUES.map((v) => (
              <li key={v} className="flex items-start gap-3 text-sm leading-snug text-[#111827]">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" strokeWidth={2.2} /> {v}
              </li>
            ))}
          </motion.ul>
        </div>
        <motion.div {...fade(0.2)} className="lg:col-span-5">
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
};
